import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axiosClient from '../../api/axiosClient';
import { 
  ServerCrash, Trash2, RefreshCw, FileJson, Eye, X, Clock, Terminal, Activity, Brain 
} from 'lucide-react';

const AgentDebugPage = () => {
  const queryClient = useQueryClient();
  //  Added 'ai_logs' to the tabs
  const [activeTab, setActiveTab] = useState('events'); // 'events', 'results', or 'ai_logs'
  const [selectedLog, setSelectedLog] = useState(null); 

  // Fetch Logs
  const { data: logs =[], isLoading, refetch } = useQuery({
    queryKey: ['agentLogs', activeTab],
    queryFn: async () => {
        try {
            // Route to the correct endpoint based on the tab
            const endpoint = activeTab === 'ai_logs' 
                ? '/admin/system/ai-logs' 
                : `/admin/agent-logs/${activeTab}`;
                
            const res = await axiosClient.get(endpoint);
            
            // AI Logs return an object { logs: [...] }, Agent logs return an array
            if (activeTab === 'ai_logs') {
                return res.logs ||[];
            }
            return Array.isArray(res) ? res : [];
        } catch(e) { return[]; }
    },
    refetchInterval: 10000
  });

  // Delete Mutation
  const clearMutation = useMutation({
    mutationFn: () => {
        const endpoint = activeTab === 'ai_logs' 
            ? '/admin/system/ai-logs' 
            : `/admin/agent-logs/${activeTab}`;
        return axiosClient.delete(endpoint);
    },
    onSuccess: () => {
        queryClient.invalidateQueries(['agentLogs']);
        alert("Logs Cleared Successfully!");
    }
  });

  // --- Helper: Robust Timestamp Formatter ---
  const formatTimestamp = (val) => {
      if (!val) return "-";
      try {
          if (typeof val === 'number') {
              const date = val > 1000000000000 ? new Date(val) : new Date(val * 1000);
              return date.toLocaleString();
          }
          return new Date(val).toLocaleString();
      } catch (e) {
          return String(val);
      }
  };

  // Helper to format AI Log lines for better readability
  const renderAILogLine = (line, index) => {
      let colorClass = "text-green-400";

      if (line.includes("DREAM") || line.includes("Self-Evolution")) {
          colorClass = "text-fuchsia-400 font-bold animate-pulse"; 
      }
      
      if (line.includes("[ERROR]") || line.includes("Crash")) colorClass = "text-red-400 font-bold";
      else if (line.includes("[WARNING]")) colorClass = "text-yellow-400";
      else if (line.includes("[THINKING]")) colorClass = "text-purple-400";
      else if (line.includes("[QUANTUM]")) colorClass = "text-blue-400 font-bold";
      
      return (
          <div key={index} className={`font-mono text-xs mb-1 ${colorClass}`}>
              {line}
          </div>
      );
  };

  return (
    <div className="space-y-6 animate-fade-in pb-10">
      
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center">
            <ServerCrash className="w-8 h-8 text-orange-600 mr-3" />
            System Blackbox
          </h1>
          <p className="text-sm text-slate-500">Raw logs captured from Agents and the Sovereign AI Engine.</p>
        </div>
        <div className="flex gap-2">
            <div className="bg-slate-100 p-1 rounded-lg flex text-sm font-medium">
                <button onClick={() => setActiveTab('events')} className={`px-4 py-2 rounded-md transition-all ${activeTab==='events' ? 'bg-white shadow text-orange-600 font-bold' : 'text-slate-500'}`}>Failed Events</button>
                <button onClick={() => setActiveTab('results')} className={`px-4 py-2 rounded-md transition-all ${activeTab==='results' ? 'bg-white shadow text-blue-600 font-bold' : 'text-slate-500'}`}>Failed Results</button>
                {/*  AI Logs Tab */}
                <button onClick={() => setActiveTab('ai_logs')} className={`px-4 py-2 rounded-md transition-all flex items-center ${activeTab==='ai_logs' ? 'bg-white shadow text-purple-600 font-bold' : 'text-slate-500'}`}>
                    <Brain className="w-4 h-4 mr-2" /> AI Consciousness
                </button>
            </div>
            <button onClick={refetch} className="p-2 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors" title="Refresh">
                <RefreshCw className="w-5 h-5"/>
            </button>
        </div>
      </div>

      {/* Log Container */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden flex flex-col h-[650px]">
          
          {/* Toolbar */}
          <div className="p-4 border-b border-slate-100 bg-slate-50 flex justify-between items-center">
              <span className="font-mono text-xs text-slate-500 uppercase font-bold flex items-center bg-white px-3 py-1 rounded border">
                  <FileJson className="w-4 h-4 mr-2 text-slate-400"/> 
                  {activeTab === 'ai_logs' ? '/logs/sentinel_consciousness.log' : `/var/agent/failed_${activeTab}.json`}
              </span>
              <button 
                onClick={() => { if(window.confirm("Clear all logs in this file?")) clearMutation.mutate() }}
                className="text-red-500 hover:bg-red-50 px-4 py-2 rounded-lg text-xs font-bold flex items-center transition-colors border border-transparent hover:border-red-100"
                disabled={logs.length === 0}
              >
                  <Trash2 className="w-4 h-4 mr-2"/> Clear File
              </button>
          </div>

          {/* Content Area */}
          <div className="flex-1 overflow-auto custom-scrollbar">
             {isLoading ? (
                 <div className="h-full flex items-center justify-center text-slate-400">Loading Blackbox Data...</div>
             ) : logs.length === 0 ? (
                 <div className="h-full flex flex-col items-center justify-center text-slate-400">
                     <Activity className="w-16 h-16 text-green-100 mb-4"/>
                     <p className="font-medium text-slate-500">System is healthy.</p>
                     <p className="text-xs text-slate-400">No logs found in storage.</p>
                 </div>
             ) : activeTab === 'ai_logs' ? (
                 /*  AI Logs Terminal View */
                 <div className="bg-[#0b0f19] min-h-full p-6">
                     {logs.map((line, i) => renderAILogLine(line, i))}
                 </div>
             ) : (
                 /* Existing Agent Logs Table */
                 <table className="w-full text-left border-collapse text-sm">
                    <thead className="bg-slate-50 text-slate-500 sticky top-0 z-10 shadow-sm">
                        <tr>
                            <th className="px-6 py-3 border-b text-xs font-bold uppercase">Time</th>
                            <th className="px-6 py-3 border-b text-xs font-bold uppercase">Type</th>
                            <th className="px-6 py-3 border-b text-xs font-bold uppercase">Process / Command</th>
                            <th className="px-6 py-3 border-b text-xs font-bold uppercase">Details</th>
                            <th className="px-6 py-3 border-b text-xs font-bold uppercase text-right">Action</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {logs.map((log, i) => {
                            const timestamp = log._ts || log.timestamp || log.created_at || log.time;
                            const type = log.event_type || log.action || log.type || 'Unknown';
                            const primaryInfo = log.comm || log.exe || log.command_name || log.command || '-';
                            
                            let details = '-';
                            if (log.pid) details = `PID: ${log.pid}`;
                            if (log.path) details = log.path;
                            if (log.dest_ip) details = `IP: ${log.dest_ip}`;
                            if (log.args && typeof log.args === 'object') details = JSON.stringify(log.args).substring(0, 30) + '...';

                            return (
                                <tr key={i} className="hover:bg-amber-50 transition-colors group">
                                    <td className="px-6 py-4 text-slate-500 whitespace-nowrap text-xs">
                                        <div className="flex items-center">
                                            <Clock className="w-3 h-3 mr-2 opacity-50"/>
                                            {formatTimestamp(timestamp)}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-blue-50 text-blue-700 border border-blue-100 uppercase">
                                            {type}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 font-mono text-xs text-slate-700">
                                        {primaryInfo}
                                    </td>
                                    <td className="px-6 py-4 text-slate-600 text-xs font-mono">
                                        {details}
                                    </td>
                                    <td className="px-6 py-4 text-right">
                                        <button 
                                            onClick={() => setSelectedLog(log)}
                                            className="text-indigo-600 hover:text-indigo-800 font-medium text-xs flex items-center justify-end w-full group-hover:underline"
                                        >
                                            <Eye className="w-4 h-4 mr-1"/> View JSON
                                        </button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                 </table>
             )}
          </div>
      </div>

      {/* --- FULL DATA MODAL --- */}
      {selectedLog && (
        <div className="fixed inset-0 bg-slate-900/60 flex items-center justify-center z-50 backdrop-blur-sm p-4 animate-in fade-in">
           <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[90vh]">
              
              <div className="p-5 border-b border-slate-100 flex justify-between items-center bg-slate-50">
                 <h3 className="font-bold text-lg text-slate-800 flex items-center">
                    <Terminal className="w-5 h-5 mr-2 text-slate-500"/> Raw Event Data
                 </h3>
                 <button onClick={() => setSelectedLog(null)} className="p-1 hover:bg-slate-200 rounded-full text-slate-500">
                    <X className="w-6 h-6"/>
                 </button>
              </div>

              <div className="p-0 overflow-auto bg-[#1e1e1e]">
                 <pre className="text-xs font-mono text-green-400 p-6 whitespace-pre-wrap leading-relaxed">
                    {JSON.stringify(selectedLog, null, 4)}
                 </pre>
              </div>
              
              <div className="p-4 border-t border-slate-100 bg-white flex justify-end">
                  <button onClick={() => setSelectedLog(null)} className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-sm font-bold">
                      Close
                  </button>
              </div>
           </div>
        </div>
      )}

    </div>
  );
};

export default AgentDebugPage;